'use client';
import { BANNER_LIST_QUERY } from '@/apollo/graphql/queries/bannerQueries/bannerQueries';
import { BRAND_LIST_QUERY } from '@/apollo/graphql/queries/brandQueries/brandQueries';
import { CATEGORY_LIST_WITH_NESTED_CHILDREN_QUERY } from '@/apollo/graphql/queries/categoryQueries/categoryQueries';
import { BrandData } from '@/apollo/graphql/types/brandTypes';
import { CategoryEdge } from '@/apollo/graphql/types/categoryTypes';
import LogoRadius from '@/assets/icons/drawer-icons/logo-radius.svg';
import {
  CollectionPublished,
  useCategoryListQuery,
  useCollectionListQuery,
} from '@/graphql';
import { decodeBase64Id } from '@/helpers/decodeBase64Id';
import { useLanguageCode } from '@/hooks/useLanguageCode';
import { useTranslatedData } from '@/hooks/useTranslatedData';
import { useRouter } from '@/i18n/navigation';
import { useQuery } from '@apollo/client';
import { useLocale, useTranslations } from 'next-intl';
import { SetStateAction, useEffect, useState } from 'react';
import styles from './drawerContent.module.scss';
import DrawerHeader from './drawerHeader';
import renderCollections from './renderCollections';
import { renderSkeletons } from '../skeleton';
import { renderCategoryList, renderCategorySecondList } from './renderCategory';
import RightSection from './rightSection';
import { brandListType } from '@/types/headerBottomDriver.type';
import RightSectionEmty from './rightSectionEmty';
import MobileVersionLinks from './mobileVersionLinks';

type DrawerContentProps = {
  closeButton: () => void;
};

const DrawerContent: React.FC<DrawerContentProps> = ({ closeButton }) => {
  const [tabSecond, setTabSecond] = useState<any>(null);
  const [tabName, setTabName] = useState<string>();
  const [showDrawer, setShowDrawer] = useState(true);
  const [tab, setTab] = useState('');
  const [isHover, setIsHover] = useState('');
  const [collectionTab, setCollectionTab] = useState('');
  const brandList: brandListType[] = [];
  const router = useRouter();
  const languageCode = useLanguageCode();
  const locale = useLocale();

  const { data: brandData, loading: brandLoading } = useQuery(
    BRAND_LIST_QUERY,
    {
      variables: { first: 100, languageCode: languageCode },
    }
  );

  const { data, loading } = useCategoryListQuery({
    variables: { first: 100, level: 0, languageCode: languageCode },
  });

  const { data: collectionData, loading: collectionLoading } =
    useCollectionListQuery({
      variables: {
        first: 100,
        filter: { published: CollectionPublished.PUBLISHED },
      },
    });

  const { data: bannerData } = useQuery(BANNER_LIST_QUERY, {
    variables: { first: 10, filter: { bannerType: 'SIDEBAR' } },
  });

  const { data: categoryData } = useQuery(
    CATEGORY_LIST_WITH_NESTED_CHILDREN_QUERY,
    {
      variables: { first: 1, filter: { ids: tab }, languageCode: languageCode },
      skip: !tab,
    }
  );

  const mappedData =
    data?.categories?.edges
      ?.filter(
        edge =>
          edge.node.parent === null &&
          edge.node.isInCatalog === true &&
          edge.node.isBottom === false
      )
      .map(edge => edge.node) || [];

  const parentCategories = useTranslatedData(mappedData, ['name']);

  const parentbottomCategories =
    data?.categories?.edges
      ?.filter(
        edge =>
          edge.node.parent === null &&
          edge.node.isInCatalog === true &&
          edge.node.isBottom === true
      )
      .map(edge => edge.node) || [];

  const handleTabChange = (index: string) => {
    setTab(index);
    setIsHover(index);
    setCollectionTab('');
  };

  const handleCollectionChange = (index: string) => {
    setCollectionTab(index);
    setIsHover('');
  };

  const handleCategoryChange = (index: string) => {
    setIsHover(index);
    setCollectionTab('');
  };

  const handleTabSecondChange = (
    categoryId: SetStateAction<string>,
    name: string
  ) => {
    setTabSecond(categoryId);
    setTab(categoryId);
    setShowDrawer(false); // Hide drawer and show mobile links
    setTabName(name);
  };

  const handleClickLogo = () => {
    router.push('/');
    closeButton();
  };

  const transformCategories = (data: any): any[] => {
    const categoryNode = data?.categories?.edges?.[0]?.node;

    if (!categoryNode) {
      return [];
    }

    const transformedCategories = [
      {
        id: categoryNode.id,
        name:
          locale === 'ru'
            ? categoryNode?.name
            : categoryNode?.translation?.name,
        edges: categoryNode.children?.edges || [],
      },
    ];

    return transformedCategories;
  };

  const transformedData = transformCategories(categoryData);

  useEffect(() => {
    if (data) {
      setTab(parentCategories[0]?.id);
    }
  }, [data]);

  const findBrandImages = (
    categoryId: string,
    brandData: BrandData | undefined
  ) => {
    if (!brandData?.brands?.edges) {
      return;
    }

    for (const brand of brandData.brands.edges) {
      const { node } = brand;

      const matchingCategory = node?.sidebarCategories?.edges.find(
        category => category?.node?.id === categoryId
      );

      if (matchingCategory) {
        brandList.push({
          id: node.id,
          image: node.image,
        });
      }
    }
  };

  const t = useTranslations('General');
  const handleRouteByCategory = (edge: CategoryEdge) => {
    closeButton();
    router.push(
      `/category/${decodeBase64Id(edge?.node?.id)}-${edge?.node?.slug}`
    );
  };

  findBrandImages(tab, brandData);

  return (
    <div style={{ height: '100%' }}>
      <div className={styles.mobileVersion}>
        {showDrawer ? (
          <>
            <DrawerHeader closeButton={closeButton} />
            <div className={styles.drawer__categories}>
              {/* collections */}
              {collectionLoading
                ? renderSkeletons(
                    5,
                    `${styles.drawer__category} ${styles.skeletonForMobile}`
                  )
                : renderCollections({
                    collections: collectionData?.collections?.edges,
                    isBottom: false,
                    closeButton,
                    handleTabChange: handleCollectionChange,
                    tab: collectionTab,
                  })}

              {loading
                ? renderSkeletons(
                    5,
                    `${styles.vacancyLoad} ${styles.skeletonForMobile}`
                  )
                : renderCategoryList({
                    categories: parentCategories,
                    handleTabSecondChange,
                    tab: isHover,
                    handleCategoryChange,
                  })}
            </div>
            <div
              className={`${styles.drawer__categories} ${styles.bottomCategories}`}
            >
              {/* collections */}
              {renderCollections({
                collections: collectionData?.collections?.edges,
                isBottom: true,
                closeButton,
                handleTabChange: handleCollectionChange,
                tab: collectionTab,
              })}

              {loading
                ? renderSkeletons(
                    5,
                    `${styles.drawer__category} ${styles.skeletonForMobile}`
                  )
                : renderCategoryList({
                    categories: parentbottomCategories,
                    handleTabSecondChange,
                    tab: isHover,
                    handleCategoryChange,
                  })}
            </div>
          </>
        ) : (
          // Mobile links view
          <MobileVersionLinks
            closeLinkButton={closeButton}
            tabName={tabName || ''}
            transformedData={transformedData}
            tab={tab}
            brandLoading={brandLoading}
            brandList={brandList}
          />
        )}
      </div>
      <div className={styles.drawer}>
        <div className={styles.drawer__leftSection}>
          <div onClick={handleClickLogo} className={styles.drawer__logo}>
            <LogoRadius />
          </div>
          <div className={styles.drawer__categories}>
            <h3>{t('catalog')}</h3>
            {/* collections */}
            {collectionLoading
              ? renderSkeletons(
                  5,
                  `${styles.drawer__category} ${styles.skeletonForMobile}`
                )
              : renderCollections({
                  collections: collectionData?.collections?.edges,
                  isBottom: false,
                  closeButton,
                  handleTabChange: handleCollectionChange,
                  tab: collectionTab,
                })}

            {/* categories */}
            {loading
              ? renderSkeletons(
                  5,
                  `${styles.drawer__category} ${styles.skeletonForMobile}`
                )
              : renderCategorySecondList({
                  categories: parentCategories,
                  closeButton,
                  handleTabChange,
                  isHover,
                })}
          </div>
          <div
            className={`${styles.drawer__categories} ${styles.bottomCategories}`}
          >
            {collectionLoading
              ? renderSkeletons(
                  5,
                  `${styles.drawer__category} ${styles.skeletonForMobile}`
                )
              : renderCollections({
                  collections: collectionData?.collections?.edges,
                  isBottom: true,
                  closeButton,
                  handleTabChange: handleCollectionChange,
                  tab: collectionTab,
                })}

            {loading
              ? renderSkeletons(
                  5,
                  `${styles.drawer__category} ${styles.skeletonForMobile}`
                )
              : renderCategorySecondList({
                  categories: parentbottomCategories,
                  closeButton,
                  handleTabChange,
                  isHover,
                })}
          </div>
        </div>
        {transformedData.length === 0 ? (
          <RightSectionEmty closeButton={closeButton} />
        ) : (
          <RightSection
            transformedData={transformedData}
            tab={tab}
            brandList={brandList}
            handleRouteByCategory={handleRouteByCategory}
            closeButton={closeButton}
            locale={locale}
            bannerData={bannerData}
          />
        )}
      </div>
    </div>
  );
};

export default DrawerContent;
